import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Tarea, EstadoTarea, TareaEvidencia } from '@/lib/types'
import { useSupabase } from '@/hooks/useSupabase'
import { IconClock, IconRefresh, IconCheckCircle, IconBan, IconTimer } from '@/components/icons/Icons'
import TareaCard from './TareaCard'

const BATCH_SIZE = 10

interface KanbanBoardProps {
  tareas: Tarea[]
  evidencias: TareaEvidencia[]
  proyectoId: number
  onTareaClick?: (tarea: Tarea) => void
  onRefresh: () => void
  onApprove: (tareaId: number) => Promise<void>
  /* Estados elegidos en el filtro. Vacio = se ven las cuatro columnas. En un
     tablero, filtrar por estado es esconder columnas, no vaciarlas. */
  estadosVisibles?: string[]
}

interface KanbanColumn {
  estado: EstadoTarea
  label: string
  icon: React.ReactNode
  color: string
}

const COLUMNS: KanbanColumn[] = [
  { estado: 'pendiente', label: 'Pendiente', icon: <IconClock size={14} />, color: 'var(--text-secondary)' },
  { estado: 'en_progreso', label: 'En Progreso', icon: <IconRefresh size={14} />, color: 'var(--info)' },
  { estado: 'completada', label: 'Completada', icon: <IconCheckCircle size={14} />, color: 'var(--success)' },
  { estado: 'bloqueada', label: 'Bloqueada', icon: <IconBan size={14} />, color: 'var(--danger)' },
]

export default function KanbanBoard({ tareas, evidencias, proyectoId, onTareaClick: _onTareaClick, onRefresh, onApprove, estadosVisibles = [] }: KanbanBoardProps) {
  const db = useSupabase()
  const nav = useNavigate()
  const [dragOverColumn, setDragOverColumn] = useState<EstadoTarea | null>(null)
  const [visibleCount, setVisibleCount] = useState<Record<string, number>>({
    pendiente: BATCH_SIZE,
    en_progreso: BATCH_SIZE,
    completada: BATCH_SIZE,
    bloqueada: BATCH_SIZE,
  })

  // Count evidencias per tarea
  const evidenciaCountMap = evidencias.reduce<Record<number, number>>((acc, ev) => {
    acc[ev.tarea_id] = (acc[ev.tarea_id] || 0) + 1
    return acc
  }, {})

  // Scroll handler for lazy loading
  const handleColumnScroll = useCallback((e: React.UIEvent<HTMLDivElement>, estado: string, totalCount: number) => {
    const el = e.currentTarget
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    if (nearBottom && visibleCount[estado] < totalCount) {
      setVisibleCount(prev => ({
        ...prev,
        [estado]: Math.min(prev[estado] + BATCH_SIZE, totalCount),
      }))
    }
  }, [visibleCount])

  function handleDragStart(e: React.DragEvent, tarea: Tarea) {
    e.dataTransfer.setData('text/plain', String(tarea.id))
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleDragOver(e: React.DragEvent, estado: EstadoTarea) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverColumn(estado)
  }

  function handleDragLeave() {
    setDragOverColumn(null)
  }

  async function handleDrop(e: React.DragEvent, nuevoEstado: EstadoTarea) {
    e.preventDefault()
    setDragOverColumn(null)

    const tareaId = Number(e.dataTransfer.getData('text/plain'))
    const tarea = tareas.find(t => t.id === tareaId)
    if (!tarea || tarea.estado === nuevoEstado) return

    try {
      const now = new Date().toISOString()
      await db.update('tareas', {
        id: tareaId,
        estado: nuevoEstado,
        fecha_completada: nuevoEstado === 'completada' ? now : null,
      })
      onRefresh()
    } catch (err) {
      console.error('Error moving task:', err)
    }
  }

  function handleTareaClick(tarea: Tarea) {
    nav(`/proyectos/${proyectoId}/tareas/${tarea.id}`)
  }

  return (
    <div className="kanban-board" id="kanban-board">
      {COLUMNS.filter(col => estadosVisibles.length === 0 || estadosVisibles.includes(col.estado)).map(col => {
        const colTareas = tareas
          .filter(t => t.estado === col.estado)
          // Hide completed+approved tasks (they go to Caja)
          .filter(t => !(t.estado === 'completada' && t.estado_aprobacion === 'aprobada'))
          .sort((a, b) => a.orden - b.orden)

        const visible = visibleCount[col.estado] || BATCH_SIZE
        const visibleTareas = colTareas.slice(0, visible)
        const remaining = colTareas.length - visible

        // Sum hours for tasks in this column
        const colHorasEst = colTareas.reduce((sum, t) => sum + (t.horas_estimadas || 0), 0)
        const colHorasReal = colTareas.reduce((sum, t) => sum + (t.horas_reales || 0), 0)

        return (
          <div
            key={col.estado}
            className={`kanban-column ${dragOverColumn === col.estado ? 'drag-over' : ''}`}
            id={`kanban-col-${col.estado}`}
            onDragOver={e => handleDragOver(e, col.estado)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, col.estado)}
          >
            <div className="kanban-column-header">
              <div>
                <span className="kanban-column-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {col.icon} {col.label}
                </span>
                {colHorasEst > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: 'var(--accent-primary)', fontWeight: 600, marginTop: 2 }}>
                    <IconTimer size={11} /> {colHorasReal > 0 ? `${colHorasReal}h / ${colHorasEst}h` : `${colHorasEst}h est.`}
                  </span>
                )}
              </div>
              <span className="kanban-column-count" style={{ color: col.color }}>
                {colTareas.length}
              </span>
            </div>
            <div
              className="kanban-column-body"
              onScroll={e => handleColumnScroll(e, col.estado, colTareas.length)}
            >
              {visibleTareas.map(tarea => (
                <TareaCard
                  key={tarea.id}
                  tarea={tarea}
                  evidenciaCount={evidenciaCountMap[tarea.id] || 0}
                  onClick={() => handleTareaClick(tarea)}
                  onDragStart={handleDragStart}
                  onApprove={col.estado === 'completada' && tarea.estado_aprobacion !== 'aprobada'
                    ? () => onApprove(tarea.id)
                    : undefined
                  }
                />
              ))}
              {remaining > 0 && (
                <button
                  className="btn btn-sm btn-secondary"
                  style={{ width: '100%', marginTop: 4, fontSize: '0.78rem', opacity: 0.8 }}
                  onClick={() => setVisibleCount(prev => ({
                    ...prev,
                    [col.estado]: prev[col.estado] + BATCH_SIZE,
                  }))}
                >
                  {remaining} más...
                </button>
              )}
              {colTareas.length === 0 && (
                <div className="kanban-empty">
                  <span>Sin tareas</span>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
